import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Col,
	Input,
	InputGroup,
	InputGroupText,
	Label,
	Row,
	Spinner,
} from "reactstrap";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import { PostData } from "../../../services/FetchData";

const AdminSettings = () => {
	const [profileData, setProfileData] = useState({});
	const [errors, setErrors] = useState({});
	const [oldPassword, setOldPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [oldPasswordShown, setOldPasswordShown] = useState(false);
	const [newPasswordShown, setNewPasswordShown] = useState(false);
	const [saveApiLoader, setSaveApiLoader] = useState(false);
	const { userData, setUserData, adminData, setAdminData, userLoader } =
		useContext(ProductContext);

	useEffect(() => {
		document.title = "Account Settings | ConversionBox Admin";
		return () => {
			document.title = "ConversionBox App";
		};
	}, []);

	useEffect(() => {
		const source = adminData || userData;
		if (!source?.email) {
			return;
		}
		setProfileData({
			firstName: source.firstName || "",
			lastName: source.lastName || "",
			email: source.email || "",
		});
	}, [adminData, userData]);

	const handleChange = (value, field) => {
		setProfileData((prev) => ({ ...prev, [field]: value }));
		if (field === "firstName" || field === "lastName") {
			setErrors((prev) => ({
				...prev,
				[field]: value.trim() ? null : `Please enter the ${field === "firstName" ? "first" : "last"} name`,
			}));
		}
	};

	const handlePassword = (value, field) => {
		if (field === "old_password") {
			setOldPassword(value);
			setErrors((prev) => ({
				...prev,
				old_password:
					!value && newPassword
						? "Password field is required"
						: value && value.length < 8
							? "Password is too short (min 8 characters)"
							: null,
			}));
			return;
		}

		setNewPassword(value);
		setErrors((prev) => ({
			...prev,
			new_password:
				!value && oldPassword
					? "Password field is required"
					: value && value.length < 8
						? "Password is too short (min 8 characters)"
						: null,
		}));
	};

	const saveChanges = () => {
		setSaveApiLoader(true);

		const nextErrors = {};
		if (!profileData.firstName?.trim()) {
			nextErrors.firstName = "Please enter the first name";
		}
		if (!profileData.lastName?.trim()) {
			nextErrors.lastName = "Please enter the last name";
		}
		if (oldPassword || newPassword) {
			if (!oldPassword) {
				nextErrors.old_password = "Password field is required";
			} else if (oldPassword.length < 8) {
				nextErrors.old_password = "Password is too short (min 8 characters)";
			}
			if (!newPassword) {
				nextErrors.new_password = "Password field is required";
			} else if (newPassword.length < 8) {
				nextErrors.new_password = "Password is too short (min 8 characters)";
			}
		}

		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors);
			setSaveApiLoader(false);
			return;
		}

		PostData("ms1", "api/v1/admin/updateProfile", {
			firstName: profileData.firstName.trim(),
			lastName: profileData.lastName.trim(),
			oldPassword: oldPassword || undefined,
			newPassword: newPassword || undefined,
		}).then((result) => {
			if (result !== "Invalid" && result !== undefined) {
				if (result.status === "success") {
					setOldPassword("");
					setNewPassword("");
					const updatedProfile = {
						firstName: profileData.firstName.trim(),
						lastName: profileData.lastName.trim(),
					};
					setUserData((prev) => ({ ...prev, ...updatedProfile }));
					setAdminData((prev) =>
						prev ? { ...prev, ...updatedProfile } : prev
					);
					toast.info(result.message);
				} else if (result.status === "failure") {
					toast.warn(result.message);
				} else if (result.status === "api_error") {
					toast.warn(result.message?.message || "Unable to update profile");
				}
			}
			setSaveApiLoader(false);
		});
	};

	if (userLoader && !profileData.email) {
		return (
			<div className="settingPageWrapper d-flex justify-content-center py-5">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="settingPageWrapper">
			<div className="d-flex justify-content-between">
				<div className="d-flex align-items-start flex-column w-100">
					<h1 className="pageMainHeading">Account Settings</h1>
				</div>
			</div>
			<div className="mt-4">
				<div className="cardDisplay cardDisplayBody">
					<div className="d-flex" id="adminDetails">
						<div className="w-25 pr4">
							<h2 className="displayHeadingInformation mb-1">Profile</h2>
						</div>
						<div className="w-75">
							<div className="formFieldWrapper gap35 d-flex flex-column mt-3">
								<Row>
									<Col xs={12} md={6}>
										<div className={`position-relative ${errors.firstName ? "error_field" : ""}`}>
											<Label className="input-group-alternative-label">First Name</Label>
											<InputGroup className="input-group-alternative">
												<Input
													placeholder="First Name"
													type="text"
													name="firstName"
													value={profileData.firstName || ""}
													onChange={(e) => handleChange(e.target.value, "firstName")}
													onBlur={(e) => handleChange(e.target.value, "firstName")}
												/>
											</InputGroup>
											{errors.firstName && (
												<div className="input-feedback">{errors.firstName}</div>
											)}
										</div>
									</Col>
									<Col xs={12} md={6}>
										<div className={`position-relative ${errors.lastName ? "error_field" : ""}`}>
											<Label className="input-group-alternative-label">Last Name</Label>
											<InputGroup className="input-group-alternative">
												<Input
													placeholder="Last Name"
													type="text"
													name="lastName"
													value={profileData.lastName || ""}
													onChange={(e) => handleChange(e.target.value, "lastName")}
													onBlur={(e) => handleChange(e.target.value, "lastName")}
												/>
											</InputGroup>
											{errors.lastName && (
												<div className="input-feedback">{errors.lastName}</div>
											)}
										</div>
									</Col>
								</Row>
								<Row>
									<Col xs={12}>
										<div className="position-relative">
											<Label className="input-group-alternative-label">Email</Label>
											<InputGroup className="input-group-alternative">
												<Input
													placeholder="Email"
													type="text"
													name="email"
													disabled
													value={profileData.email || ""}
													className="disabled"
												/>
											</InputGroup>
										</div>
									</Col>
								</Row>
							</div>
						</div>
					</div>
					<hr className="cpBorderTop cbBorderGrey my-4" />
					<div className="d-flex" id="adminPassword">
						<div className="w-25 pr4">
							<h2 className="displayHeadingInformation mb-1">Update Password</h2>
						</div>
						<div className="w-75">
							<div className="formFieldWrapper gap35 d-flex flex-column mt-3">
								<Row>
									<Col xs={12} md={6}>
										<div className={`position-relative passwordField ${errors.old_password ? "error_field" : ""}`}>
											<Label className="input-group-alternative-label">Current password</Label>
											<InputGroup className="input-group-alternative">
												<Input
													placeholder="Current password"
													type={oldPasswordShown ? "text" : "password"}
													name="old_password"
													value={oldPassword}
													onChange={(e) => handlePassword(e.target.value, "old_password")}
													onBlur={(e) => handlePassword(e.target.value, "old_password")}
													autoComplete="current-password"
												/>
												<InputGroupText>
													<div
														className="showPasswordWrapper"
														onClick={() => setOldPasswordShown(!oldPasswordShown)}
													>
														<span className="material-icons-outlined">
															{oldPasswordShown ? "visibility" : "visibility_off"}
														</span>
													</div>
												</InputGroupText>
											</InputGroup>
											{errors.old_password && (
												<div className="input-feedback">{errors.old_password}</div>
											)}
										</div>
									</Col>
									<Col xs={12} md={6}>
										<div className={`position-relative passwordField ${errors.new_password ? "error_field" : ""}`}>
											<Label className="input-group-alternative-label">New password</Label>
											<InputGroup className="input-group-alternative">
												<Input
													placeholder="New password"
													type={newPasswordShown ? "text" : "password"}
													name="new_password"
													value={newPassword}
													onChange={(e) => handlePassword(e.target.value, "new_password")}
													onBlur={(e) => handlePassword(e.target.value, "new_password")}
													autoComplete="new-password"
												/>
												<InputGroupText>
													<div
														className="showPasswordWrapper"
														onClick={() => setNewPasswordShown(!newPasswordShown)}
													>
														<span className="material-icons-outlined">
															{newPasswordShown ? "visibility" : "visibility_off"}
														</span>
													</div>
												</InputGroupText>
											</InputGroup>
											{errors.new_password && (
												<div className="input-feedback">{errors.new_password}</div>
											)}
										</div>
									</Col>
								</Row>
							</div>
						</div>
					</div>
					<hr className="cpBorderTop cbBorderGrey my-4" />
					<div className="d-flex align-items-center justify-content-end">
						<Button
							color="primary"
							className="accountSettingSaveBtn cta autoWidth"
							disabled={saveApiLoader}
							onClick={saveChanges}
						>
							{saveApiLoader && <Spinner size="sm" />}
							Save Changes
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminSettings;
